import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import { Warning, X, ArrowRight, Gear, Check } from "@phosphor-icons/react";

/**
 * LocalAlertBanner — slim orange link pinned to the top of every page.
 *
 * Renders as a single-line strip:
 *   ⚠  Severe thunderstorm warning · Portland, OR · +2 more  →  ⚙  ×
 *
 * - Reads location from `readLocation()` (LocationPill).
 * - Polls `/api/weather/alerts/local?...` every 10 min so the message is
 *   always current.
 * - Advisory Preferences: a small gear popover lets farmers pick which
 *   severities (Severe / Warning / Watch) actually trigger the top bar, so
 *   quieter regions don't get pinged as often. Saved device-local.
 * - Dismiss is remembered per-alert (localStorage).
 * - Hidden on /notifications + admin/steward routes.
 */
const DISMISS_KEY = "np:weather_alert_dismissed";
const SEV_PREFS_KEY = "np:alert_severity_prefs";
const SNOOZE_KEY = "np:alert_snooze_until";
const SUPPRESS_ROUTES = ["/notifications", "/admin", "/steward"];

// backend severity vocab → farmer-facing label
const SEV_OPTIONS = [
  { key: "severe", label: "Severe", hint: "Cyclones, floods, tornadoes — act now" },
  { key: "warning", label: "Warning", hint: "Storms, heatwaves, hail — prepare" },
  { key: "advisory", label: "Watch", hint: "Lower-urgency advisories — good to know" },
];

function readDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeDismissed(set) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set].slice(-100))); }
  catch { /* ignore */ }
}
function readSevPrefs() {
  try {
    const p = JSON.parse(localStorage.getItem(SEV_PREFS_KEY) || "null");
    if (p && typeof p === "object") {
      return { severe: p.severe !== false, warning: p.warning !== false, advisory: !!p.advisory };
    }
  } catch { /* ignore */ }
  return { severe: true, warning: true, advisory: false }; // default: severe + warning
}
function readSnooze() {
  try { return Number(localStorage.getItem(SNOOZE_KEY)) || 0; }
  catch { return 0; }
}

export default function LocalAlertBanner() {
  const { pathname } = useLocation();
  const [rawAlerts, setRawAlerts] = useState([]);
  const [loc, setLoc] = useState(readLocation);
  const [prefs, setPrefs] = useState(readSevPrefs);
  const [snoozeUntil, setSnoozeUntil] = useState(readSnooze);
  const [showPrefs, setShowPrefs] = useState(false);
  const popRef = useRef(null);
  const suppressed = SUPPRESS_ROUTES.some((r) => pathname.startsWith(r));

  useEffect(() => {
    const onChange = (e) => setLoc(e.detail || readLocation());
    window.addEventListener("np:location-changed", onChange);
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  // Close the prefs popover on outside-click / Escape.
  useEffect(() => {
    if (!showPrefs) return;
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setShowPrefs(false); };
    const onKey = (e) => { if (e.key === "Escape") setShowPrefs(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, [showPrefs]);

  useEffect(() => {
    if (!loc || !loc.lat || !loc.lon || suppressed) { setRawAlerts([]); return; }
    let alive = true;
    const dismissed = readDismissed();
    const pull = async () => {
      try {
        const { data } = await http.get(
          `/weather/alerts/local?lat=${loc.lat}&lon=${loc.lon}&radius_km=250&limit=8`,
        );
        if (!alive) return;
        // Keep every severity — the severity filter is applied client-side so
        // farmers can change their preference without a refetch.
        setRawAlerts((data.alerts || []).filter((a) => !dismissed.has(a.id)));
      } catch { /* silent — banner is optional */ }
    };
    pull();
    const t = setInterval(pull, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [loc, suppressed]);

  const persistPrefs = (next) => {
    setPrefs(next);
    try { localStorage.setItem(SEV_PREFS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const togglePref = (key) => persistPrefs({ ...prefs, [key]: !prefs[key] });

  const snooze = (hours) => {
    const until = Date.now() + hours * 3600 * 1000;
    try { localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* ignore */ }
    setSnoozeUntil(until);
    setShowPrefs(false);
  };

  // Auto-wake the bar when a snooze window expires.
  useEffect(() => {
    if (!snoozeUntil) return;
    const ms = snoozeUntil - Date.now();
    if (ms <= 0) { setSnoozeUntil(0); return; }
    const t = setTimeout(() => setSnoozeUntil(0), ms);
    return () => clearTimeout(t);
  }, [snoozeUntil]);

  const alerts = useMemo(() => rawAlerts.filter((a) => prefs[a.severity]), [rawAlerts, prefs]);

  const snoozed = snoozeUntil > Date.now();
  if (snoozed) return null;
  if (!rawAlerts.length) return null;

  const first = alerts[0];
  const severeCount = alerts.filter((a) => a.severity === "severe").length;
  const isSevere = severeCount > 0;
  const hiddenCount = rawAlerts.length - alerts.length;

  const dismissAll = (e) => {
    e.preventDefault(); e.stopPropagation();
    const s = readDismissed(); alerts.forEach((a) => s.add(a.id)); writeDismissed(s);
    setRawAlerts((prev) => prev.filter((a) => !prefs[a.severity]));
  };

  const bg = first ? (isSevere ? "#B85F2A" : "#F9C784") : "#EFE3CE";
  const fg = first ? (isSevere ? "#FFFFFF" : "#241209") : "#5B4632";

  const prefsButton = (
    <div className="relative shrink-0" ref={popRef}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrefs((v) => !v); }}
        data-testid="local-alert-prefs-toggle"
        aria-label="Advisory preferences"
        aria-expanded={showPrefs}
        className="p-0.5 opacity-70 hover:opacity-100"
        style={{ color: fg }}
      >
        <Gear size={14} weight="bold" />
      </button>
      {showPrefs && (
        <div
          data-testid="local-alert-prefs-popover"
          className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-black/12 bg-white text-[color:var(--np-text,#151312)] shadow-2xl p-3 z-[95]"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1.5">
            Alert me about
          </div>
          <p className="text-xs text-[color:var(--np-text-2,#3A3830)] leading-snug mb-2.5">
            Choose which weather severities show in this top bar.
          </p>
          <div className="space-y-1.5">
            {SEV_OPTIONS.map(({ key, label, hint }) => {
              const on = !!prefs[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePref(key)}
                  data-testid={`local-alert-pref-${key}`}
                  aria-pressed={on}
                  className={`w-full flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                    on
                      ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)]/8"
                      : "border-black/12 hover:bg-black/[0.03]"
                  }`}
                >
                  <span
                    className={`mt-0.5 shrink-0 grid place-items-center h-4 w-4 rounded border ${
                      on ? "bg-[color:var(--np-brand,#B85F2A)] border-[color:var(--np-brand,#B85F2A)] text-white" : "border-black/30 text-transparent"
                    }`}
                  >
                    <Check size={11} weight="bold" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-tight">{label}</span>
                    <span className="block text-[11px] text-[color:var(--np-text-2,#3A3830)] leading-snug">{hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-black/10">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-text-2,#3A3830)] mb-1.5">
              Mute the bar
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => snooze(1)}
                data-testid="local-alert-snooze-1h"
                className="flex-1 rounded-full border border-black/15 px-3 py-1.5 text-xs font-semibold hover:bg-black/[0.04] transition-colors"
              >
                1 hour
              </button>
              <button
                type="button"
                onClick={() => snooze(24)}
                data-testid="local-alert-snooze-24h"
                className="flex-1 rounded-full border border-black/15 px-3 py-1.5 text-xs font-semibold hover:bg-black/[0.04] transition-colors"
              >
                24 hours
              </button>
            </div>
          </div>
          <p className="mt-2.5 text-[11px] text-[color:var(--np-text-2,#3A3830)] leading-snug">
            Saved on this device.
          </p>
        </div>
      )}
    </div>
  );

  // No alert matches the current preferences, but advisories exist — show a
  // muted strip so the gear stays reachable and farmers can re-enable levels.
  if (!first) {
    return (
      <div
        data-testid="local-alert-banner"
        className="fixed top-0 left-0 right-0 z-[90]"
        style={{ background: bg, color: fg }}
      >
        <div className="flex items-center gap-2 px-3 md:px-5 py-1.5 max-w-6xl mx-auto text-[12px]">
          <Warning size={12} weight="fill" className="shrink-0 opacity-70" />
          <span className="truncate">
            {hiddenCount} local weather {hiddenCount === 1 ? "advisory" : "advisories"} hidden by your preferences
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {prefsButton}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="local-alert-banner"
      className="fixed top-0 left-0 right-0 z-[90]"
      style={{ background: bg, color: fg }}
    >
      <div className="flex items-center gap-2 px-3 md:px-5 py-1.5 max-w-6xl mx-auto text-[13px]">
        <Link
          to={first.url || "/notifications"}
          data-testid="local-alert-primary"
          className="flex items-center gap-2 min-w-0 grow hover:underline"
        >
          <Warning size={13} weight="fill" className="shrink-0" />
          <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase shrink-0">
            {isSevere ? "Severe weather" : "Advisory"}
          </span>
          <span className="truncate">
            {first.title}
            {loc?.city && <span className="opacity-80"> · {loc.city}{loc.state ? `, ${loc.state}` : ""}</span>}
            {alerts.length > 1 && <span className="opacity-80"> · +{alerts.length - 1} more</span>}
          </span>
          <ArrowRight size={12} weight="bold" className="shrink-0" />
        </Link>
        <span className="shrink-0 flex items-center gap-1.5 ml-1">
          {prefsButton}
          <button
            type="button"
            onClick={dismissAll}
            data-testid="local-alert-dismiss-all"
            aria-label="Dismiss weather alerts"
            className="shrink-0 opacity-70 hover:opacity-100"
            style={{ color: fg }}
          >
            <X size={12} weight="bold" />
          </button>
        </span>
      </div>
    </div>
  );
}
